// VoiceInputButton — the one mic control every "dictate into a text field"
// surface reuses (Conversations composer, Recognition message, later task/notes
// comments). Speech → editable text: it calls onTranscript with the recognized
// text and NEVER sends/posts anything itself. Kept decoupled from Dana — an
// optional "Ask Dana to improve" action stays a separate control on the field.
import React from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Mic, Square, Loader2, X } from 'lucide-react';
import { useVoiceInput } from '../hooks/useVoiceInput';

interface Props {
  /** Called with the transcribed text. The caller appends it to its draft. */
  onTranscript: (text: string) => void;
  /** IETF locale hint (falls back to the stored preferred-locale). */
  locale?: string;
  /** Compact icon size in px (default 18). */
  size?: number;
  className?: string;
  disabled?: boolean;
  /** Label shown to screen readers on the idle button. */
  ariaLabel?: string;
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const VoiceInputButton: React.FC<Props> = ({
  onTranscript, locale, size = 18, className = '', disabled, ariaLabel,
}) => {
  const { t } = useTranslation(); // defaultNS = 'common'
  const { state, elapsed, start, stop, cancel } = useVoiceInput({
    onTranscript,
    locale,
    onError: (m) => toast.error(m),
  });

  const btn =
    'inline-flex items-center justify-center rounded-full transition-colors ' +
    'text-muted-foreground hover:text-charcoal hover:bg-muted disabled:opacity-40 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

  if (state === 'recording') {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <button
          type="button"
          onClick={stop}
          className={`${btn} h-8 gap-1.5 px-2 text-red-600 hover:text-red-700`}
          aria-label={t('voice.stop')}
          title={t('voice.stop')}
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" aria-hidden />
          <Square size={size - 4} fill="currentColor" aria-hidden />
          <span className="font-mono text-xs tabular-nums" aria-live="off">{mmss(elapsed)}</span>
        </button>
        <button
          type="button"
          onClick={cancel}
          className={`${btn} h-8 w-8`}
          aria-label={t('voice.cancel')}
          title={t('voice.cancel')}
        >
          <X size={size - 2} aria-hidden />
        </button>
      </span>
    );
  }

  if (state === 'processing' || state === 'requesting') {
    return (
      <button
        type="button"
        disabled
        className={`${btn} h-8 w-8 ${className}`}
        aria-label={t('voice.transcribing')}
        title={t('voice.transcribing')}
        aria-busy
      >
        <Loader2 size={size} className="animate-spin" aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className={`${btn} h-8 w-8 ${className}`}
      aria-label={ariaLabel || t('voice.start')}
      title={ariaLabel || t('voice.start')}
      aria-pressed={false}
    >
      <Mic size={size} aria-hidden />
    </button>
  );
};
